import { TimeZoneAndLocale } from '../types.custom'
import moment, { Moment } from 'moment-timezone'

//
// Confluence i18n and l10n
//  - https://confluence.comsysto.com/pages/viewpage.action?pageId=127832565
//
// Custom Hook to work with dateTime locale and timezone aware
//  - https://reactjs.org/docs/hooks-custom.html
//
// utcDateString from database should be in format: 2019-02-03T14:00:00.000Z
//
// Note when using moment, moment is not immutable !always create new copies of a date
//

type useDateTimeResult = {
  formatDateShort: (utcDateString: string, utc?: boolean) => string | undefined
  formatDateShortWithTimezone: (utcDateString: string, tz: string) => string | undefined
  formatDate: (utcDateString: string) => string | undefined
  formatDateTimeShort: (utcDateString: string) => string | undefined
  formatDateTime: (utcDateString: string) => string | undefined
  formatTimeShort: (utcDateString: string) => string | undefined
  formatTimestamp: (utcDateString: string) => string | undefined
  formatTimestampWithTimezone: (utcDateString: string, tz: string) => string | undefined
  formatDateMonthYear: (utcDateString: string, utc?: boolean) => string | undefined
  formatDateMonthYearShort: (utcDateString: string, utc?: boolean) => string | undefined
  formatDateDayMonth: (utcDateString: string, utc?: boolean) => string | undefined
  formatDateToUtcString: (date: Date) => string
  formatDateYear: (utcDateString: string) => string | undefined
  formatDateToIsoString: (stringDate?: string, referenceTimezone?: string, daysToAdd?: number) => string | undefined
  calcStartOfLastYear: () => Date
  calcStartOfNextNYear: (nYears: number) => Moment
  calcStartDateOfNextNYear: (nYears: number) => Date
  calcEndOfNextNYear: (nYears: number) => Moment
  getEndOfDay: () => Moment
  getListOfMonths: () => string[]
  isSameDay: (utcDateString1: string, utcDateString2: string) => boolean
  isToday: (utcDateString: string) => boolean
  isWithinNextWeek: (utcDateString: string) => boolean
  isWithinPastWeek: (utcDateString: string) => boolean
  isAfterNowWithMargin: (utcDateString: string) => boolean
  compareDates: (utcDateStringA: string, utcDateStringB: string) => number
  isInLastWeek: (utcDateString: string) => boolean
  now: () => Moment
  timeZoneNow: () => Moment
  subtractWeeks: (weeksToSubtract: number) => Moment
  subtractMonths: (monthsToSubtract: number) => Moment
  addMonths: (monthToAdd: number) => Moment
  utcDateStringToMoment: (utcDateString: string) => Moment | undefined
  momentToUtcDateString: (moment1: Moment) => string | undefined
  getCurrentYear: (utcDateString: string) => number | undefined
  getNextYear: (utcDateString: string) => number | undefined
  getLastYear: (utcDateString: string) => number | undefined
  datesOverlap: (aStart: string, aEnd: string, bStart: string, bEnd: string) => boolean
  localesWith12HourTimeFormat: string[]
  calcStartOfTheYearMoment: (year: number) => Date
  calcEndOfTheYearMoment: (year: number) => Date
  utcDateStringToMomentWithoutTimezone: (utcDateString: string) => Moment
}

export const useDateTime = (timeZoneAndLocale: TimeZoneAndLocale): useDateTimeResult => {
  moment.locale(timeZoneAndLocale.locale)
  // UTC Formats a browser Date (meaning a date in any timezone to UTC)
  const formatDateToUtcString = (date: Date): string => {
    if (!date) return ''
    return new Date(date.toUTCString()).toISOString()
  }

  const utcDateStringToMoment = (utcDateString: string): Moment | undefined => {
    if (!utcDateString || utcDateString.length === 0) return undefined
    return moment(utcDateString).tz(timeZoneAndLocale.timezone)
  }

  // formats: 2019-02-03T14:00:00.000Z
  const momentToUtcDateString = (moment1: Moment): string | undefined => {
    if (!moment1) return undefined
    return moment(moment1).tz('UTC').format('YYYY-MM-DDTHH:mm:00.000') + 'Z'
  }

  const formatDateToIsoString = (stringDate?: string, referenceTimezone?: string, daysToAdd?: number): string | undefined => {
    if (!stringDate) {
      return undefined
    }
    const momentDate: Moment = moment(stringDate)
      .tz(referenceTimezone || timeZoneAndLocale.timezone)
      .startOf('day')
    return daysToAdd ? momentDate.add(daysToAdd, 'days').toISOString() : momentDate.toISOString()
  }

  // Formats Year,Month,Day like "01.01.2018"
  const formatDateShortWithTimezone = (utcDateString: string, tz: string): string | undefined => {
    let dateObj = moment(utcDateString)
    if (!dateObj.isValid()) {
      return undefined
    } else {
      dateObj = dateObj.tz(tz)
      if (!dateObj) {
        console.error(`An invalid time zone has been provided: "${tz}".`)
        return undefined
      }
      return dateObj.format('L')
    }
  }

  // Formats Year,Month,Day like "01.01.2018"
  const formatDateShort = (utcDateString: string, utc?: boolean): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj && utc ? dateObj.utc().format('L') : dateObj ? dateObj.format('L') : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Hour,Minute like "15:00"
  const formatTimeShort = (utcDateString: string): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj ? dateObj.format('LT') : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year,Month,Day like "Februray 2, 2019"
  const formatDate = (utcDateString: string): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj ? dateObj.format('LL') : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year,Month,Day,Hour,Minute like "05/29/1453, 12:00 AM"
  const formatDateTimeShort = (utcDateString: string): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj ? dateObj.format('L, LT') : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year,Month,Day,Hour,Minute like "May 29, 1453, 12:00:00 AM"
  const formatDateTime = (utcDateString: string): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj ? dateObj.format('LL, LT') : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year,Month,Day,Hour,Minute like "05/29/1453, 12:00 AM (UTC+2)"
  const formatTimestamp = (utcDateString: string): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj ? dateObj.format('L, LT (UTCZ)') : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year,Month,Day,Hour,Minute like "05/29/1453, 12:00 AM (UTC+2)"
  const formatTimestampWithTimezone = (utcDateString: string, tz: string): string | undefined => {
    try {
      const dateObj = moment(utcDateString).tz(tz)
      return dateObj ? dateObj.format('L, LT (UTCZ)') : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year,Month like "Februray, 2019"
  const formatDateMonthYear = (utcDateString: string, utc?: boolean): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj && utc
        ? dateObj.locale('en').utc().format('MMMM, YYYY')
        : dateObj
        ? dateObj.locale('en').format('MMMM, YYYY')
        : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year,Month like "01. 2019"
  const formatDateMonthYearShort = (utcDateString: string, utc?: boolean): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      const separator = getDateLocaleSeparator(utcDateString)
      return dateObj && utc ? dateObj.utc().format(`MM${separator}YYYY`) : dateObj ? dateObj.format(`MM${separator}YYYY`) : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Day and Month like "25/08"
  const formatDateDayMonth = (utcDateString: string, utc?: boolean): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      const separator = getDateLocaleSeparator(utcDateString)
      const splittedDate =
        dateObj && utc ? dateObj.utc().format('L').split(separator) : dateObj ? dateObj.format('L').split(separator) : undefined

      // Remove year from splittedDate array
      const splittedDateWithoutYear = splittedDate
        ? splittedDate.filter((dateComponent) => {
            return dateComponent.length === 2
          })
        : undefined

      // Return day and month sorted by locale. Write dateObj.format(`DD${separator}MM`) it is not enough because some countries,
      // like the USA, write the month before the day and ambiguity may arise in distinguishing month to day.
      // Therefore the order between month and day should not alter.
      return splittedDateWithoutYear ? `${splittedDateWithoutYear[0]}${separator}${splittedDateWithoutYear[1]}` : undefined
    } catch (err) {
      return undefined
    }
  }

  // Formats Year like "2019"
  const formatDateYear = (utcDateString: string): string | undefined => {
    try {
      const dateObj = utcDateStringToMoment(utcDateString)
      return dateObj ? dateObj.format('YYYY') : undefined
    } catch (err) {
      return undefined
    }
  }

  const calcStartOfLastYear = (): Date => {
    return moment().utc().subtract(1, 'year').startOf('year').toDate()
  }

  const calcStartOfNextNYear = (nYears: number): Moment => {
    return moment().utc().add(nYears, 'year').startOf('year')
  }
  const calcStartDateOfNextNYear = (nYears: number): Date => {
    return moment().utc().add(nYears, 'year').startOf('year').toDate()
  }

  const calcEndOfNextNYear = (nYears: number): Moment => {
    return moment().utc().add(nYears, 'year').endOf('year').startOf('day')
  }

  const getEndOfDay = (): Moment => {
    return moment().endOf('day')
  }

  const getListOfMonths = (): string[] => {
    return moment.months()
  }
  //
  // Date checks
  //
  const isSameDay = (utcDateString1: string, utcDateString2: string): boolean => {
    const dateObj = utcDateStringToMoment(utcDateString1)
    return dateObj ? dateObj.isSame(utcDateString2, 'day') : false
  }

  const isToday = (utcDateString: string): boolean => {
    // FIXME: does not respect timezone!
    const nowObj = moment()
    const dateObj = utcDateStringToMoment(utcDateString)
    return dateObj ? dateObj.isSame(nowObj, 'day') : false
  }

  // date is next week or today
  const isWithinNextWeek = (utcDateString: string): boolean => {
    // FIXME: does not respect timezone!
    const nowObj = moment()
    const dateObj = utcDateStringToMoment(utcDateString)
    return dateObj ? dateObj.isBefore(nowObj.endOf('day').add(7, 'days')) : false
  }

  // date is not older than one week, but may be sometimes in the future
  const isWithinPastWeek = (utcDateString: string): boolean => {
    // FIXME: does not respect timezone!
    const nowObj = moment()
    const dateObj = utcDateStringToMoment(utcDateString)
    return dateObj ? dateObj.isAfter(nowObj.endOf('day').subtract(7, 'days')) : false
  }

  const isAfterNowWithMargin = (utcDateString: string): boolean => {
    // FIXME: does not respect timezone!
    const nowObj = moment()
    const dateObj = utcDateStringToMoment(utcDateString)
    return dateObj ? nowObj.diff(dateObj) < 6000 : false // added a margin of a minute
  }

  const compareDates = (utcDateStringA: string, utcDateStringB: string): number => {
    const dateObjA = utcDateStringToMoment(utcDateStringA)
    const dateObjB = utcDateStringToMoment(utcDateStringB)
    return dateObjA && dateObjB ? dateObjA.diff(dateObjB) : -1
  }

  // date is exactly last week (including today)
  const isInLastWeek = (utcDateString: string): boolean => {
    // FIXME: does not respect timezone!
    // note moment mutable!!!, never trust that you will receive a new date
    const dateObj = utcDateStringToMoment(utcDateString)
    const dateSevenDaysAgo = moment().subtract(7, 'day').endOf('day')
    const dateEndOfToday = moment().endOf('day')
    if (!dateObj) return false
    return dateObj.isAfter(dateSevenDaysAgo) && dateObj.isBefore(dateEndOfToday)
  }

  const getCurrentYear = (utcDateString: string): number | undefined => {
    const dateObj = utcDateStringToMoment(utcDateString)
    return dateObj ? dateObj.toDate().getUTCFullYear() : undefined
  }

  const getNextYear = (utcDateString: string): number | undefined => {
    const dateObj = utcDateStringToMoment(utcDateString)
    return dateObj ? dateObj.toDate().getUTCFullYear() + 1 : undefined
  }
  const getLastYear = (utcDateString: string): number | undefined => {
    const dateObj = utcDateStringToMoment(utcDateString)
    return dateObj ? dateObj.toDate().getUTCFullYear() - 1 : undefined
  }

  const datesOverlap = (aStart: string, aEnd: string, bStart: string, bEnd: string): boolean => {
    const _aStart = utcDateStringToMoment(aStart)
    const _aEnd = utcDateStringToMoment(aEnd)
    const _bStart = utcDateStringToMoment(bStart)
    const _bEnd = utcDateStringToMoment(bEnd)

    if (!_aStart || !_aEnd || !_bStart || !_bEnd) {
      return false
    }

    // B ends in A
    if (_aStart.valueOf() < _bEnd.valueOf() && _bEnd.valueOf() <= _aEnd.valueOf()) {
      return true
    }

    // B starts in A
    if (_aStart.valueOf() <= _bStart.valueOf() && _bStart.valueOf() < _aEnd.valueOf()) {
      return true
    }

    // B in A
    if (_bStart.valueOf() >= _aStart.valueOf() && _bEnd.valueOf() <= _aEnd.valueOf()) {
      return true
    }

    // A in B
    if (_aStart.valueOf() >= _bStart.valueOf() && _aEnd.valueOf() <= _bEnd.valueOf()) {
      return true
    }

    return false
  }

  //
  // Manipulations
  //
  const now = (): Moment => moment() // FIXME: All not timezone aware
  const timeZoneNow = (): Moment => moment().tz(timeZoneAndLocale.locale)

  const subtractWeeks = (weeksToSubtract: number): Moment => {
    return moment().subtract(weeksToSubtract, 'weeks')
  }
  const subtractMonths = (monthsToSubtract: number): Moment => {
    return moment().subtract(monthsToSubtract, 'months')
  }
  const addMonths = (monthToAdd: number): Moment => moment().add(monthToAdd, 'months')

  const getDateLocaleSeparator = (utcDateString: string): string => {
    const date = moment(utcDateString).format('L')
    if (!date) {
      return '-'
    }
    return date.indexOf('.') > -1 ? '.' : date.indexOf('/') > -1 ? '/' : '-'
  }

  // Add new locales here if they use AM/PM time format
  const localesWith12HourTimeFormat = ['en_US', 'en-US']

  const calcStartOfTheYearMoment = (year: number): Date => {
    return moment().year(year).utc().startOf('year').toDate()
  }

  const calcEndOfTheYearMoment = (year: number): Date => {
    return moment().year(year).utc().endOf('year').startOf('day').toDate()
  }

  const utcDateStringToMomentWithoutTimezone = (utcDateString: string): Moment => {
    const realPointInTime = moment(utcDateString)
    return moment(realPointInTime.format('YYYY-MM-DD'))
  }

  return {
    formatDateShort,
    formatDateShortWithTimezone,
    formatDate,
    formatDateTimeShort,
    formatDateTime,
    formatTimeShort,
    formatTimestamp,
    formatTimestampWithTimezone,
    formatDateMonthYear,
    formatDateMonthYearShort,
    formatDateDayMonth,
    formatDateToUtcString,
    formatDateYear,
    formatDateToIsoString,
    calcStartOfLastYear,
    calcStartOfNextNYear,
    calcStartDateOfNextNYear,
    calcEndOfNextNYear,
    getEndOfDay,
    getListOfMonths,
    isSameDay,
    isToday,
    isWithinNextWeek,
    isWithinPastWeek,
    isAfterNowWithMargin,
    compareDates,
    isInLastWeek,
    now,
    timeZoneNow,
    subtractWeeks,
    subtractMonths,
    addMonths,
    utcDateStringToMoment,
    momentToUtcDateString,
    getCurrentYear,
    getNextYear,
    getLastYear,
    datesOverlap,
    localesWith12HourTimeFormat,
    calcStartOfTheYearMoment,
    calcEndOfTheYearMoment,
    utcDateStringToMomentWithoutTimezone,
  }
}
